import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Divider,
  Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import ScrollableDialog from '../common/ScrollableDialog';
import PrimaryActionButton from '../common/PrimaryActionButton';
import SwitchField from '../common/SwitchField';
import { fieldLabelSx, inputWrapperSx } from '../common/formFieldStyles';
import { revalidateServiceRow } from '../../utils/freshaServiceCsv';

const emptyDraft = {
  freshaServiceId: '',
  name: '',
  durationMinutes: '',
  price: '',
  description: '',
  disabled: false,
};

const inputStyle = (hasError = false) => ({
  width: '100%',
  padding: '12px 16px',
  border: `2px solid ${hasError ? '#f44336' : '#e0e0e0'}`,
  borderRadius: '8px',
  fontSize: '16px',
  fontFamily: 'inherit',
  outline: 'none',
  transition: 'border-color 0.3s ease',
  backgroundColor: '#fff',
  boxSizing: 'border-box',
});

const FixFailedImportServicesDialog = ({
  open,
  onClose,
  rows,
  onApply,
}) => {
  const { t } = useTranslation();
  const [selectedRowNumber, setSelectedRowNumber] = useState(null);
  const [draft, setDraft] = useState(emptyDraft);
  const [fieldErrors, setFieldErrors] = useState([]);

  const failingRows = useMemo(() => rows.filter((row) => row.errors.length > 0), [rows]);

  useEffect(() => {
    if (!open) {
      return;
    }

    const firstRow = failingRows[0];
    if (!firstRow) {
      setSelectedRowNumber(null);
      setDraft(emptyDraft);
      return;
    }

    setSelectedRowNumber(firstRow.rowNumber);
    setDraft({
      freshaServiceId: firstRow.freshaServiceId || '',
      name: firstRow.name || '',
      durationMinutes: firstRow.durationMinutes ?? '',
      price: firstRow.price ?? '',
      description: firstRow.description || '',
      disabled: Boolean(firstRow.disabled),
    });
    setFieldErrors([]);
  }, [open, failingRows]);

  const selectedRow = failingRows.find((row) => row.rowNumber === selectedRowNumber);

  const handleApply = () => {
    if (!selectedRow) {
      return;
    }

    const parsedDuration = Number.parseInt(draft.durationMinutes, 10);
    const parsedPrice = draft.price === '' ? 0 : Number.parseFloat(draft.price);
    const nextRow = revalidateServiceRow({
      ...selectedRow,
      freshaServiceId: draft.freshaServiceId.trim(),
      name: draft.name.trim(),
      durationMinutes: parsedDuration,
      price: parsedPrice,
      description: draft.description.trim(),
      disabled: Boolean(draft.disabled),
    });

    if (nextRow.errors.length > 0) {
      setFieldErrors(nextRow.errors);
      return;
    }

    onApply(nextRow);
    setFieldErrors([]);
  };

  return (
    <ScrollableDialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      showCloseButton
      dividers
      title={t('dataImport.services.fixDialog.title')}
      actions={(
        <>
          <Button onClick={onClose} color="inherit">{t('common.cancel')}</Button>
          <PrimaryActionButton onClick={handleApply} disabled={!selectedRow}>
            {t('dataImport.services.fixDialog.apply')}
          </PrimaryActionButton>
        </>
      )}
    >
      {failingRows.length === 0 ? (
        <Typography>{t('dataImport.services.fixDialog.noFailures')}</Typography>
      ) : (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
            {failingRows.map((row) => (
              <Button
                key={row.rowNumber}
                variant={row.rowNumber === selectedRowNumber ? 'contained' : 'outlined'}
                onClick={() => {
                  setSelectedRowNumber(row.rowNumber);
                  setDraft({
                    freshaServiceId: row.freshaServiceId || '',
                    name: row.name || '',
                    durationMinutes: row.durationMinutes ?? '',
                    price: row.price ?? '',
                    description: row.description || '',
                    disabled: Boolean(row.disabled),
                  });
                  setFieldErrors([]);
                }}
                sx={{ textTransform: 'none' }}
              >
                {t('dataImport.services.fixDialog.rowLabel', { row: row.rowNumber, name: row.name || '—' })}
              </Button>
            ))}
          </Box>

          <Divider />

          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2 }}>
            <Box>
              <Typography sx={fieldLabelSx}>{t('dataImport.services.fields.serviceId')} *</Typography>
              <Box
                component="input"
                value={draft.freshaServiceId}
                onChange={(event) => setDraft((prev) => ({ ...prev, freshaServiceId: event.target.value }))}
                sx={{ ...inputStyle(fieldErrors.includes('dataImport.services.errors.serviceIdRequired')), ...inputWrapperSx }}
              />
            </Box>
            <Box>
              <Typography sx={fieldLabelSx}>{t('dataImport.services.fields.name')} *</Typography>
              <Box
                component="input"
                value={draft.name}
                onChange={(event) => setDraft((prev) => ({ ...prev, name: event.target.value }))}
                sx={{ ...inputStyle(fieldErrors.includes('dataImport.services.errors.nameRequired')), ...inputWrapperSx }}
              />
            </Box>
            <Box>
              <Typography sx={fieldLabelSx}>{t('dataImport.services.fields.duration')} *</Typography>
              <Box
                component="input"
                type="number"
                min={1}
                value={draft.durationMinutes}
                onChange={(event) => setDraft((prev) => ({ ...prev, durationMinutes: event.target.value }))}
                sx={{ ...inputStyle(fieldErrors.some((key) => key.includes('duration'))), ...inputWrapperSx }}
              />
            </Box>
            <Box>
              <Typography sx={fieldLabelSx}>{t('dataImport.services.fields.price')}</Typography>
              <Box
                component="input"
                type="number"
                min={0}
                step="0.01"
                value={draft.price}
                onChange={(event) => setDraft((prev) => ({ ...prev, price: event.target.value }))}
                sx={{ ...inputStyle(), ...inputWrapperSx }}
              />
            </Box>
            <Box sx={{ gridColumn: '1 / -1' }}>
              <Typography sx={fieldLabelSx}>{t('dataImport.services.fields.description')}</Typography>
              <Box
                component="textarea"
                rows={3}
                value={draft.description}
                onChange={(event) => setDraft((prev) => ({ ...prev, description: event.target.value }))}
                sx={{ ...inputStyle(), ...inputWrapperSx, minHeight: 96 }}
              />
            </Box>
            <Box sx={{ gridColumn: '1 / -1' }}>
              <SwitchField
                label={t('dataImport.services.fields.disabled')}
                checked={draft.disabled}
                onChange={(event) => setDraft((prev) => ({ ...prev, disabled: event.target.checked }))}
              />
            </Box>
          </Box>

          {fieldErrors.length > 0 ? (
            <Alert severity="error">
              {fieldErrors.map((key) => t(key)).join(' · ')}
            </Alert>
          ) : null}
        </Box>
      )}
    </ScrollableDialog>
  );
};

export default FixFailedImportServicesDialog;
